import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { ContextService } from 'src/core/services/context.service';
import { computed } from 'mobx';

export class QuoteEntryListDataService extends DataService<typeof Model.Bookings.Logic.ViewModels.QuoteEntrySummary, Model.Bookings.Logic.ViewModels.QuoteEntrySummary, QuoteEntrySummary> {
	protected map(entity: Model.Bookings.Logic.ViewModels.QuoteEntrySummary): QuoteEntrySummary {
		return new QuoteEntrySummaryModel(entity);
	}

	static make(context?: ContextService): QuoteEntryListDataService {
		return new QuoteEntryListDataService(context, ctx => ctx.QuoteEntrySummaries);
	}

	async getBreakdown(quoteEntryId: string): Promise<BreakdownEntry[]> {
		const container = await this.contextService.current();
		const breakdown = await container.GetBreakdown(quoteEntryId).toArray();

		return breakdown.map(m => ({
			id: m.id,
			quoteEntryId: m.quoteEntryId,
			quotePaxId: m.quotePaxId,
			productDisplayName: m.productDisplayName,
			clientDisplayName: m.clientDisplayName,
			price: parseFloat(m.price),
			unitPrice: parseFloat(m.unitPrice)
		}));
	}
}

export class QuoteEntrySummaryModel extends EntityModel<Model.Bookings.Logic.ViewModels.QuoteEntrySummary, QuoteEntrySummary> implements QuoteEntrySummary {
	@property<QuoteEntrySummaryModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.dateFrom, (o, value) => o.entity.dateFrom = value)
	public dateFrom: Date;

	@property<QuoteEntrySummaryModel>(o => o.entity.dateTo, (o, value) => o.entity.dateTo = value)
	public dateTo: Date;

	@property<QuoteEntrySummaryModel>(o => o.entity.status, (o, value) => o.entity.status = value)
	public status: number;

	@property<QuoteEntrySummaryModel>(o => o.entity.productType, (o, value) => o.entity.productType = value)
	public productType: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.costPrice.toString()), (o, value) => o.entity.costPrice = value)
	public costPrice: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.sellPrice.toString()), (o, value) => o.entity.sellPrice = value)
	public sellPrice: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.salePriceGross.toString()), (o, value) => o.entity.salePriceGross = value)
	public salePriceGross: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.salesmanProfit.toString()), (o, value) => o.entity.salesmanProfit = value)
	public salesmanProfit: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.calculatedCostPrice.toString()), (o, value) => o.entity.calculatedCostPrice = value)
	public calculatedCostPrice: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.calculatedSalePriceGross.toString()), (o, value) => o.entity.calculatedSalePriceGross = value)
	public calculatedSalePriceGross: number;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.invoicedCost.toString()), (o, value) => o.entity.invoicedCost = value)
	public invoicedCost: number;

	@property<QuoteEntrySummaryModel>(o => o.entity.confirmationReferenceNumber, (o, value) => o.entity.confirmationReferenceNumber = value)
	public confirmationReferenceNumber: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.productId, (o, value) => o.entity.productId = value)
	public productId: number;

	@property<QuoteEntrySummaryModel>(o => o.entity.productDisplayName, (o, value) => o.entity.productDisplayName = value)
	public productDisplayName: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.supplierId, (o, value) => o.entity.supplierId = value)
	public supplierId: number;

	@property<QuoteEntrySummaryModel>(o => o.entity.supplierTitle, (o, value) => o.entity.supplierTitle = value)
	public supplierTitle: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.quoteId, (o, value) => o.entity.quoteId = value)
	public quoteId: number;

	@property<QuoteEntrySummaryModel>(o => o.entity.bookingReference, (o, value) => o.entity.bookingReference = value)
	public bookingReference: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.bookingStatus, (o, value) => o.entity.bookingStatus = value)
	public bookingStatus: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.reservationDate, (o, value) => o.entity.reservationDate = value)
	public reservationDate: Date;

	@property<QuoteEntrySummaryModel>(o => o.entity.clientsDisplayNames, (o, value) => o.entity.clientsDisplayNames = value)
	public clientsDisplayNames: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.costCurrencyCode, (o, value) => o.entity.costCurrencyCode = value)
	public costCurrencyCode: string;

	@property<QuoteEntrySummaryModel>(o => o.entity.sellCurrencyCode, (o, value) => o.entity.sellCurrencyCode = value)
	public sellCurrencyCode: string;

	@property<QuoteEntrySummaryModel>(o => parseFloat(o.entity.sellExchangeRate.toString()), (o, value) => o.entity.sellExchangeRate = value)
	public sellExchangeRate: number;

	@property<QuoteEntrySummaryModel>(o => JSON.parse(o.entity.occupancy), (o, value) => o.entity.occupancy = value)
	public occupancy: QuoteEntryOccupancy[];

	@computed
	public get occupancyText(): string {
		let adults = 0;
		let children = 0;
		let infants = 0;
		for (const pax of this.occupancy) {
			switch (pax.type) {
				case 0:
					adults++;
					break;
				case 1:
					children++;
					break;
				case 2:
					infants++;
					break;
			}
		}
		return `${adults > 0 ? adults + "A" : ""}${children > 0 ? children + "C" : ""}${infants > 0 ? infants + "I" : ""}`;
	}

	@property<QuoteEntrySummaryModel>(o => o.entity.quote, (o, value) => o.entity.quote = value)
	public quote: QuoteItem;


	public get accrualValue(): number {
		return parseFloat(this.costPrice.toString()) - parseFloat(this.invoicedCost.toString());
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/cost/parts/quote-entry-list.data.ts